import { supabase } from '@/lib/supabase';
import type {
  CampaignStatus,
  CampaignsInsert,
  CampaignsRow,
  CampaignsUpdate,
} from '@/types/database.types';

export interface CampaignsFilters {
  search?: string;
  status?: CampaignStatus;
}

export interface CampaignsListResult {
  rows: CampaignsRow[];
  count: number;
}

const PAGE_SIZE = 25;

export async function listCampaigns(
  filters: CampaignsFilters,
  page: number,
): Promise<CampaignsListResult> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('campaigns')
    .select('*', { count: 'exact' })
    .is('deleted_at', null)
    .is('migrated_from', null)
    .order('updated_at', { ascending: false })
    .range(from, to);

  if (filters.search) {
    query = query.ilike('name', `%${filters.search}%`);
  }
  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { rows: (data ?? []) as CampaignsRow[], count: count ?? 0 };
}

/**
 * Leads conseguidos por la campaña: conversaciones de lead que nacieron de
 * una respuesta a esta campaña (lead_conversations.campaign_id).
 */
export async function countCampaignLeads(campaignId: string): Promise<number> {
  const { count, error } = await supabase
    .from('lead_conversations')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', campaignId);
  if (error) throw error;
  return count ?? 0;
}

export async function getCampaign(id: string): Promise<CampaignsRow | null> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', id)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as CampaignsRow | null) ?? null;
}

export async function createCampaign(
  input: Omit<CampaignsInsert, 'owner_user_id'> & { owner_user_id: string },
): Promise<CampaignsRow> {
  const { data, error } = await supabase
    .from('campaigns')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data as CampaignsRow;
}

export async function updateCampaign(
  id: string,
  patch: CampaignsUpdate,
): Promise<CampaignsRow> {
  const { data, error } = await supabase
    .from('campaigns')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as CampaignsRow;
}

/**
 * Archivar = solo cambiar status a 'archived'. La campaña SIGUE visible en
 * el tab "Archivadas" del listado y en su propia ruta /campaigns/:id.
 *
 * NO setea deleted_at: ese flag está reservado para el hard-delete (que
 * directamente borra la fila vía hardDeleteCampaign). El historial de mails
 * enviados queda intacto para que se pueda consultar.
 */
export async function softDeleteCampaign(id: string): Promise<void> {
  const { error } = await supabase
    .from('campaigns')
    .update({ status: 'archived' })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Borrado físico de una campaña. Solo permitido si NO tiene emails con
 * status terminal (sent/delivered/opened/clicked/replied/bounced/complained).
 * Hace un re-check en cliente para no depender únicamente del contador
 * cacheado en campaigns.total_sent (puede estar desactualizado).
 *
 * El FK ON DELETE CASCADE de campaign_touches/email_variants/campaign_emails
 * se encarga del resto.
 */
export async function hardDeleteCampaign(id: string): Promise<void> {
  // Defense in depth: bloquear hard-delete de campañas legacy (migradas).
  // El RLS server-side también lo bloquea, pero cortamos en cliente con un
  // mensaje claro en vez de esperar el 403.
  const { data: row, error: rowErr } = await supabase
    .from('campaigns')
    .select('migrated_from')
    .eq('id', id)
    .maybeSingle();
  if (rowErr) throw new Error(`No se pudo verificar la campaña: ${rowErr.message}`);
  if (row?.migrated_from) {
    throw new Error(
      'Esta campaña fue importada (legacy). No se puede eliminar para preservar la historia.',
    );
  }

  const { count, error: countErr } = await supabase
    .from('campaign_emails')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', id)
    .in('status', [
      'sent',
      'delivered',
      'opened',
      'clicked',
      'replied',
      'bounced',
      'complained',
    ]);
  if (countErr) throw new Error(`No se pudo verificar la cola: ${countErr.message}`);
  if ((count ?? 0) > 0) {
    throw new Error(
      `Esta campaña tiene ${count} emails enviados. No se puede borrar para no perder la historia.`,
    );
  }

  const { error } = await supabase.from('campaigns').delete().eq('id', id);
  if (error) throw error;
}

export async function countCampaignsByStatus(): Promise<
  Partial<Record<CampaignStatus, number>>
> {
  const { data, error } = await supabase
    .from('campaigns')
    .select('status')
    .is('deleted_at', null)
    .is('migrated_from', null);
  if (error) throw error;
  const counts: Partial<Record<CampaignStatus, number>> = {};
  for (const row of data ?? []) {
    const s = (row as { status: CampaignStatus }).status;
    counts[s] = (counts[s] ?? 0) + 1;
  }
  return counts;
}
